import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import apiService from '../services/apiService';
import {
  ContentCard,
  CardTitle,
  FormGroup,
  ImageUpload,
  ImagePreview,
  ActionButtons,
  SaveButton,
  CancelButton,
  DeleteButton,
  SuccessMessage,
  ErrorMessage,
  ServicesList,
  ServiceItem,
  ServiceHeader
} from '../styles/AdminStyles';

const NewsPostManager = forwardRef((props, ref) => {
  const [newsPosts, setNewsPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [loadingImages, setLoadingImages] = useState({});

  // Expose fetchNewsPosts method to parent components
  useImperativeHandle(ref, () => ({
    fetchNewsPosts
  }));
  
  // Fetch all news posts
  const fetchNewsPosts = async () => {
    setLoading(true);
    try {
      const posts = await apiService.getNewsPosts();
      setNewsPosts(posts);
    } catch (error) {
      console.error('Error fetching news posts:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load news posts. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Load news posts on component mount
  useEffect(() => {
    fetchNewsPosts();
  }, []);

  // Load images for a post using lazy loading
  const loadPostImages = async (postId) => {
    // Don't reload if images are already loading or loaded
    if (loadingImages[postId]) return;
    
    setLoadingImages(prev => ({ ...prev, [postId]: true }));
    try {
      const images = await apiService.getNewsPostImages(postId);
      setNewsPosts(prev => 
        prev.map(post => 
          post._id === postId ? { ...post, images } : post
        )
      );
    } catch (error) {
      console.error(`Error loading images for post ${postId}:`, error);
    } finally {
      setLoadingImages(prev => ({ ...prev, [postId]: false }));
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Enter edit mode for a post
  const handleEdit = (post) => {
    // Load images if not already loaded
    if (!post.images && post._id) {
      loadPostImages(post._id);
    }
    setCurrentPost(post);
    setEditMode(true);
  };

  // Exit edit mode
  const handleCancelEdit = () => {
    setEditMode(false);
    setCurrentPost(null);
  };

  // Handle input change for edit form
  const handleEditInputChange = (field, value) => {
    setCurrentPost(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle image upload in edit mode
  const handleEditImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Process each file
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCurrentPost(prev => ({
          ...prev,
          images: [...(prev.images || []), reader.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove image in edit mode
  const removeEditImage = (index) => {
    setCurrentPost(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Update post
  const handleUpdate = async () => {
    if (!currentPost) return;
    
    if (!currentPost.text.trim()) {
      setMessage({ type: 'error', text: 'Please enter post text.' });
      return;
    }

    if (!currentPost.images || currentPost.images.length === 0) {
      setMessage({ type: 'error', text: 'Please upload at least one image.' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await apiService.updateNewsPost(currentPost._id, currentPost);
      setMessage({ type: 'success', text: 'News post updated successfully!' });
      setEditMode(false);
      setCurrentPost(null);
      fetchNewsPosts(); // Refresh the list
    } catch (error) {
      console.error('Error updating news post:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Error updating news post. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete post
  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this news post?')) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await apiService.deleteNewsPost(postId);
      setMessage({ type: 'success', text: 'News post deleted successfully!' });
      fetchNewsPosts(); // Refresh the list
    } catch (error) {
      console.error('Error deleting news post:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Error deleting news post. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContentCard>
      <CardTitle>Manage News Posts</CardTitle>
      
      {message && (
        message.type === 'success' ? (
          <SuccessMessage>{message.text}</SuccessMessage>
        ) : (
          <ErrorMessage>{message.text}</ErrorMessage>
        )
      )}

      {editMode && currentPost ? (
        // Edit form
        <>
          <FormGroup>
            <label>Post Date</label>
            <input
              type="date"
              value={currentPost.postDate ? currentPost.postDate.split('T')[0] : ''}
              onChange={(e) => handleEditInputChange('postDate', e.target.value)}
            />
          </FormGroup>
          
          <FormGroup>
            <label>Post Text</label>
            <textarea
              value={currentPost.text || ''}
              onChange={(e) => handleEditInputChange('text', e.target.value)}
              placeholder="Enter news post content"
              rows="5"
            />
          </FormGroup>
          
          <FormGroup>
            <label>Images</label>
            <ImageUpload onClick={() => document.getElementById('edit-news-images').click()}>
              <input
                type="file"
                id="edit-news-images"
                multiple
                accept="image/*"
                onChange={handleEditImageUpload}
              />
              <p>Click to upload more images</p>
            </ImageUpload>
            
            {currentPost.images && currentPost.images.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <p>Image Previews:</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {currentPost.images.map((img, index) => (
                    <ImagePreview key={index} style={{ position: 'relative', width: '150px' }}>
                      <img 
                        src={img} 
                        alt={`Preview ${index + 1}`} 
                        style={{ width: '100%', height: '100px', objectFit: 'cover' }} 
                      />
                      <button 
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          background: 'rgba(255,0,0,0.7)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '25px',
                          height: '25px',
                          cursor: 'pointer'
                        }}
                        onClick={() => removeEditImage(index)}
                      >
                        ×
                      </button>
                    </ImagePreview>
                  ))}
                </div>
              </div>
            )}
          </FormGroup>
          
          <ActionButtons>
            <SaveButton onClick={handleUpdate} disabled={loading}>
              {loading ? 'Saving...' : 'Update Post'}
            </SaveButton>
            <CancelButton onClick={handleCancelEdit}>Cancel</CancelButton>
          </ActionButtons>
        </>
      ) : (
        // News posts list
        <>
          {loading ? (
            <p>Loading news posts...</p>
          ) : newsPosts.length === 0 ? (
            <p>No news posts available.</p>
          ) : (
            <ServicesList>
              {newsPosts.map((post) => (
                <ServiceItem 
                  key={post._id}
                  onMouseEnter={() => post._id && !post.images && loadPostImages(post._id)}
                >
                  <ServiceHeader>
                    <h4>{formatDate(post.postDate || post.createdAt)}</h4>
                    <div>
                      <button 
                        style={{
                          background: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '5px 10px',
                          marginRight: '10px',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleEdit(post)}
                      >
                        Edit
                      </button>
                      <DeleteButton onClick={() => handleDelete(post._id)}>Delete</DeleteButton>
                    </div>
                  </ServiceHeader>
                  
                  <div>
                    <p>{post.text}</p>
                    
                    {post.images ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                        {post.images.map((img, index) => (
                          <img 
                            key={index}
                            src={img} 
                            alt={`News post ${index + 1}`} 
                            style={{ width: '100px', height: '75px', objectFit: 'cover' }}
                          />
                        ))}
                      </div>
                    ) : (
                      <p>Loading images...</p>
                    )}
                  </div>
                </ServiceItem>
              ))}
            </ServicesList>
          )}
        </>
      )}
    </ContentCard>
  );
});

export default NewsPostManager;
